// A small pool of WASM workers with cancellation (terminate + respawn).

import type { Method, Methods, WorkerRequest, WorkerResponse } from './protocol';
import WasmWorker from './worker.ts?worker';

export class CancelledError extends Error {
  constructor() {
    super('cancelled');
    this.name = 'CancelledError';
  }
}

interface Pending {
  resolve: (v: unknown) => void;
  reject: (e: Error) => void;
  worker: number;
}

interface Queued {
  req: WorkerRequest;
  transfer: Transferable[];
  pending: Omit<Pending, 'worker'>;
}

export class WorkerPool {
  private workers: Worker[] = [];
  private busy: boolean[] = [];
  private pending = new Map<number, Pending>();
  private queue: Queued[] = [];
  private nextId = 1;

  constructor(readonly size: number) {
    for (let i = 0; i < size; i++) this.spawn(i);
  }

  private spawn(i: number) {
    const w = new WasmWorker();
    w.onmessage = (ev: MessageEvent<WorkerResponse>) => this.onMessage(i, ev.data);
    w.onerror = (ev) => {
      ev.preventDefault();
      this.failWorker(i, new Error(ev.message || 'worker error'));
    };
    this.workers[i] = w;
    this.busy[i] = false;
  }

  private onMessage(i: number, msg: WorkerResponse) {
    const p = this.pending.get(msg.id);
    this.pending.delete(msg.id);
    this.busy[i] = false;
    if (p) {
      if (msg.ok) p.resolve(msg.result);
      else p.reject(new Error(msg.error));
    }
    this.pump();
  }

  private failWorker(i: number, err: Error) {
    for (const [id, p] of this.pending) {
      if (p.worker === i) {
        this.pending.delete(id);
        p.reject(err);
      }
    }
    this.busy[i] = false;
    this.pump();
  }

  private pump() {
    for (let i = 0; i < this.workers.length && this.queue.length; i++) {
      if (this.busy[i]) continue;
      const job = this.queue.shift()!;
      this.busy[i] = true;
      this.pending.set(job.req.id, { ...job.pending, worker: i });
      this.workers[i].postMessage(job.req, job.transfer);
    }
  }

  call<M extends Method>(
    method: M,
    payload: Methods[M]['req'],
    transfer: Transferable[] = [],
  ): Promise<Methods[M]['res']> {
    return new Promise((resolve, reject) => {
      const req: WorkerRequest<M> = { id: this.nextId++, method, payload };
      this.queue.push({ req, transfer, pending: { resolve: resolve as (v: unknown) => void, reject } });
      this.pump();
    });
  }

  /** Rejects everything queued or running and restarts the workers. */
  cancelAll() {
    const err = new CancelledError();
    for (const q of this.queue) q.pending.reject(err);
    this.queue = [];
    for (const p of this.pending.values()) p.reject(err);
    this.pending.clear();
    for (let i = 0; i < this.workers.length; i++) {
      this.workers[i].terminate();
      this.spawn(i);
    }
  }

  get queued(): number {
    return this.queue.length + this.pending.size;
  }
}

/** Default parallelism: leave one core for the UI thread. */
export function defaultPoolSize(): number {
  const n = typeof navigator !== 'undefined' ? navigator.hardwareConcurrency || 4 : 4;
  return Math.max(1, Math.min(n - 1, 12));
}

let interactive: WorkerPool | null = null;
let batch: WorkerPool | null = null;

/** Single worker for interactive previews (cancelled on every change). */
export function interactivePool(): WorkerPool {
  return (interactive ??= new WorkerPool(1));
}

/** Multi-worker pool for frame batches (animation). */
export function batchPool(): WorkerPool {
  return (batch ??= new WorkerPool(defaultPoolSize()));
}
